import type { FilterTemplateProps, MultiSelectFilterField } from '../../types';
import { useDatasetConfig } from '../../dataset-context';
import {
  useModalFacets,
  useCategoryFields,
  useHiddenWhileEmpty,
} from '../template-hooks';
import { orderFacetItems } from '../facet-label';
import { useIsFilterRail } from '../context';
import { FacetMultiSelect } from '../facet-multi-select';
import { FieldLabel } from '../field-label';
import { PasteListControl } from 'components/paste-list';
import style from '../filter-modal.module.scss';

interface MultiSelectFieldProps {
  field: MultiSelectFilterField;
  formValues: Record<string, unknown>;
  onFormChange: (key: string, value: unknown) => void;
}

/**
 * One searchable multi-select filter, with Paste List outside the rail.
 * @param {MultiSelectFieldProps} props The filter.
 * @return {JSX.Element | null} The filter.
 */
function MultiSelectField({
  field,
  formValues,
  onFormChange,
}: MultiSelectFieldProps) {
  const facets = useModalFacets();
  const rail = useIsFilterRail();
  const config = useDatasetConfig();

  // Labelled before ordering, so options sort as they read.
  const buckets = orderFacetItems(
    (facets?.[field.facetKey]?.buckets ?? []).map((bucket) =>
      field.optionLabel
        ? { ...bucket, name: field.optionLabel(bucket.name) }
        : bucket
    ),
    !!config.alphabeticalFacetOptions,
    { numeric: field.numericOrder }
  );
  const selectedIds = (formValues[field.param] as string[] | undefined) ?? [];
  const hidden = useHiddenWhileEmpty(
    buckets.length > 0,
    selectedIds.length > 0
  );
  if (hidden) return null;

  /**
   * Stages the chosen ids, unset when none are chosen.
   * @param {string[]} ids The chosen option ids.
   */
  const setSelected = (ids: string[]) =>
    onFormChange(field.param, ids.length > 0 ? ids : undefined);

  return (
    <div>
      {field.helperText && (
        <div className={style.fieldLabelRow}>
          <FieldLabel field={field} />
        </div>
      )}
      <FacetMultiSelect
        id={field.id}
        titleText={field.label}
        hideLabel={!!field.helperText}
        placeholder={
          field.placeholder ?? `Select ${field.label.toLowerCase()}...`
        }
        facetBuckets={buckets}
        selectedIds={selectedIds}
        showCounts={!rail}
        compact={rail}
        onChange={setSelected}
      />
      {!rail && (
        <PasteListControl
          id={`multi-select-${field.id}`}
          label={field.label}
          options={buckets}
          selectedIds={selectedIds}
          onApply={setSelected}
        />
      )}
    </div>
  );
}

/**
 * The category's searchable multi-select filters.
 * @param {FilterTemplateProps} props The template props.
 * @return {JSX.Element} The filters.
 */
export function MultiSelectTemplate({
  category,
  formValues,
  onFormChange,
}: FilterTemplateProps) {
  const fields = useCategoryFields(category, 'multi-select');

  return (
    <>
      {fields.map((field) => (
        <MultiSelectField
          key={field.id}
          field={field}
          formValues={formValues}
          onFormChange={onFormChange}
        />
      ))}
    </>
  );
}
